import { NgIf } from '@angular/common'
import { HttpErrorResponse } from '@angular/common/http'
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core'
import { ActivatedRoute } from '@angular/router'
import { saveAs } from '@progress/kendo-file-saver'
import { pdfOpenUrl, showPdfInWindow } from './pdf-window.util'
import { ContextMenuSettingsModel, FileManagerAllModule, FileManagerComponent, FileManagerModule, ToolbarSettingsModel, UploadSettingsModel } from '@syncfusion/ej2-angular-filemanager'
import {
  BookmarkViewService,
  LinkAnnotationService,
  MagnificationService,
  NavigationService,
  PrintService,
  TextSearchService,
  TextSelectionService,
  ThumbnailViewService,
  ToolbarService,
} from '@syncfusion/ej2-angular-pdfviewer'
import { registerLicense } from '@syncfusion/ej2-base'
import * as CryptoJS from 'crypto-js'
import { throwError } from 'rxjs'
import { environment } from '../../../environments/environment'
import { FileServices } from '../../@theme/services/file.service'
import { PDFSERVICE } from '../../@theme/services/pdfgetfiles.service'
import { DialogService } from './service/btnbutton.service'
registerLicense(
  'ORg4AjUWIQA/Gnt3VVhhQlJDfV5AQmBIYVp/TGpJfl96cVxMZVVBJAtUQF1hTH5bd0FiWH5bcXZSR2NYWkd+',
)
@Component({
    selector: 'gd-filemanager2',
    templateUrl: './filemanager.component.html',
    styleUrls: ['./filemanager.component.scss'],
    providers: [
        LinkAnnotationService,
        BookmarkViewService,
        MagnificationService,
        ThumbnailViewService,
        ToolbarService,
        NavigationService,
        TextSearchService,
        TextSelectionService,
        PrintService,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgIf, FileManagerModule, FileManagerAllModule]
})
export class Filemanager2Component implements OnInit, OnDestroy {
  public ajaxSettings: object = new Object()
  private authLocalStorageToken = `${environment.appVersion}-${environment.USERDATA_KEY}`;
  public service = `${environment.apiUrl}/api/pdfviewer`
  //Default document to render in the PdfViewerControl
  public document = ''
  private pdfData: Blob
  
  private handleError(error: HttpErrorResponse) {
    console.error('FileManager API Error:', error);
    
    if (error.error instanceof ErrorEvent) {
      console.error('Client-side error:', error.error.message);
    } else {
      console.error(`Backend error code: ${error.status}, body: ${JSON.stringify(error.error)}`);
    }
    
    return throwError(() => new Error('Something went wrong with the FileManager API. Please try again later.'));
  }

  public contextMenuSettings: ContextMenuSettingsModel = {
    file: ['Open', '|', 'Cut', 'Copy', '|', 'Delete', 'Rename'],
    folder: [
      'Open',
      '|',
      'Cut',
      'Copy',
      'Paste',
      '|',
      'Delete',
      'Rename',
    ],
    layout: [
      'SortBy',
      'View',
      'Refresh',
      '|',
      'Paste',
      '|',
      'SelectAll',
    ],
    visible: true,
  }
  public toolbarSettings: ToolbarSettingsModel
  public uploadSettings: UploadSettingsModel
  @ViewChild('filetransaction')
  public filetransaction: FileManagerComponent
  public view: string
  public hostUrl: string = environment.apiUrl
  private ContentType: string
  public fileManagerReady = false
  public allowDragAndDrop?: boolean;
  @Input()
  outputClientId: string
  @Input()
  outputfiletrans: string
  showclientname: boolean
  @Input()
  outputrepId: string
  @Input()
  outquery: string
  public getpaths: string
  @Input()
  blotterId: string
  blot: boolean
  public firstname: string
  public lastname: string
  @Input()
  clientName: string
  public marketingIds: string
  @Input()
  viewaccountsscreen: boolean
  @Input()
  viewallblot: string
  public rolesplit: string
  public roletips: string
  private cookiepath: string
  @Input()
  set marketingId(value: string) {
    this.marketingIds = value
  }

  get marketingId(): string {
    return this.marketingIds
  }
  public BlotterName: number = 0

  // Names of files actually uploaded during this FileManager session.
  // Posted to /api/Transactions/CheckingFile when the component is destroyed
  // (i.e. when the FileManager window is closed) so the backend can send the
  // "transaction needs processing" alert based on what was just uploaded.
  private uploadedFiles: string[] = []

  constructor(
    protected cd: ChangeDetectorRef,
    private file: FileServices,
    private pdfService: PDFSERVICE,
    private route: ActivatedRoute,
    private dservice: DialogService,
  ) {
    this.blot = false
    if (localStorage.getItem('blotterid') === 'undefined') {
      localStorage.removeItem('blotterid')
    }
    const storageValueReturn = localStorage.getItem('rvarietylist') ?? ''
    this.roletips = CryptoJS.AES.decrypt(
      storageValueReturn,
      environment.securepw,
    ).toString(CryptoJS.enc.Utf8)
  }

  ngOnInit(): void {
    // Check API connection first
    this.checkApiConnection();
    
    this.getFolderFiles()
    this.contextMenuSettings = {
      file: [
        'Open',
        'Download',
        '|',
        'Cut',
        'Copy',
        '|',
        'Delete',
        'Rename',
      ],
      folder: [
        'Open',
        '|',
        'Cut',
        'Copy',
        'Paste',
        '|',
        'Delete',
        'Rename',
      ],
      layout: [
        'SortBy',
        'View',
        'Refresh',
        '|',
        'Paste',
        '|',
        'SelectAll',
      ],
      visible: true,
    }
    if (decodeURIComponent(this.roletips) === "ADVISOR PRINCIPAL") {
      this.toolbarSettings = { items: ['Upload'], visible: true }
    } else {
      this.toolbarSettings = { items: ['Upload'], visible: false }
    }
    this.fileManagerReady = true
    this.dservice.closeButtonClicked$.subscribe(() => {
      // Do something when the close button in the dialog is clicked
    })
  }

  /** Get Files on load */
  getFolderFiles() {
    // Fix the conditional assignment
    this.marketingIds = this.marketingIds || '0';
    let query = this.outquery || '';
    
    if (this.outputfiletrans === undefined) {
      this.outputfiletrans = '';
    }
    
    if (this.blotterId !== undefined) {
      localStorage.setItem('blotterid', this.blotterId);
    }
    
    try {
      // Properly get auth token following the codebase pattern
      const currentUser = JSON.parse(localStorage.getItem(this.authLocalStorageToken) || '{}');
      const cookiepath = currentUser?.token || '';
      // Set proper API endpoints       alert(JSON.stringify(this.viewallblot))
      this.ajaxSettings = {
        url: `${this.hostUrl}/api/FileManagerCredentials/FileOperations?repid=${this.outputrepId}&clientid=${this.outputClientId}&blotterid=${this.blotterId}&marketingId=${this.marketingId}&ClientName=${this.clientName}&ViewAll=${this.viewallblot}&viewaccountsscreen=${this.viewaccountsscreen}`,
        getImageUrl: `${this.hostUrl}/api/FileManagerCredentials/GetImage`,
        uploadUrl: `${this.hostUrl}/api/FileManagerCredentials/Upload?repid=${this.outputrepId}&clientid=${this.outputClientId}&blotterid=${this.blotterId}&marketingId=${this.marketingId}&ClientName=${this.clientName}&ViewAll=${this.viewallblot}`,
        downloadUrl: `${this.hostUrl}/api/FileManagerCredentials/Download?t=${cookiepath}`,
      };
      
      this.view = 'Details';
      this.allowDragAndDrop = true;
      console.log('FileManager settings initialized:', this.ajaxSettings);
      this.cd.markForCheck()
    } catch (err) {
      console.error('Error initializing FileManager:', err);
    }
  }

  beforeSend(args: any) {
    if (!args || !args.ajaxSettings) {
      console.error('Invalid args in beforeSend');
      return;
    }

    try {
      const action = (args.action || '').toString().toLowerCase();
      console.log('[FM diag] beforeSend action =', args.action, '| data isFormData =', (typeof FormData !== 'undefined' && args.ajaxSettings.data instanceof FormData));

      // Process path for toolbar visibility (read only - upload payload is FormData, not JSON)
      if (action === 'read') {
        const data = JSON.parse(args.ajaxSettings.data || '{}');
        const path = data[0] === undefined ? (data.path || '/') : data[0].path;

        if (path !== '/') {
          this.toolbarSettings = { items: ['Upload'], visible: true };
        } else {
          const userRole = decodeURIComponent(this.roletips || '');
          const hasUploadPermission = userRole === "ADVISOR PRINCIPAL" || userRole === "ADVISOR USER";

          this.toolbarSettings = {
            items: hasUploadPermission ? ['Upload'] : [],
            visible: true
          };
        }
      }

      // Capture filenames being uploaded. Syncfusion FileManager's (success)
      // event does not fire for uploads (the embedded Uploader bypasses it),
      // so beforeSend is the only reliable hook for the upload XHR.
      if (action === 'upload') {
        this.captureUploadFileNames(args.ajaxSettings.data);
      }

      // Get auth token properly
      const currentUser = JSON.parse(localStorage.getItem(this.authLocalStorageToken) || '{}');
      const token = currentUser?.token || '';

      // Set auth header directly in the headers object
      args.ajaxSettings.headers = {
        ...(args.ajaxSettings.headers || {}),
        'Authorization': `Bearer ${token}`
      };

    } catch (error) {
      console.error('Error in beforeSend:', error);
    }
  }

  private captureUploadFileNames(data: any): void {
    const push = (name: string) => {
      if (name && this.uploadedFiles.indexOf(name) === -1) {
        this.uploadedFiles.push(name);
      }
    };

    if (typeof FormData !== 'undefined' && data instanceof FormData) {
      const entries = (data as any).entries ? (data as any).entries() : null;
      if (entries) {
        for (const [, value] of entries) {
          if (value && typeof (value as any).name === 'string') {
            push((value as any).name);
          }
        }
      }
      return;
    }

    if (typeof data === 'string') {
      try {
        const parsed = JSON.parse(data);
        const items = Array.isArray(parsed) ? parsed : [parsed];
        for (const item of items) {
          if (item?.name) push(item.name);
          else if (item?.fileName) push(item.fileName);
        }
      } catch {
        // not JSON - ignore
      }
    }
  }

  beforeImageLoad(args: any) {
    const currentUser = JSON.parse(localStorage.getItem(this.authLocalStorageToken) || '{}')
    const cookiepath = currentUser?.token || ''
    args.imageUrl = `${args.imageUrl}&t=${cookiepath}`
  }

  checkApiConnection() {
    const testUrl = `${this.hostUrl}/api/health/check`;
    
    // Use a simple fetch to test API connection
    fetch(testUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
    .then(response => {
      if (response.ok) {
        console.log('API connection successful');
      } else {
        console.error(`API connection failed: ${response.status}`);
        this.handleConnectionError(response.status);
      }
    })
    .catch(error => {
      console.error('API connection error:', error);
      this.handleConnectionError();
    });
  }

  handleConnectionError(statusCode?: number) {
    let errorMessage = 'Unable to connect to file management services. ';
    
    if (statusCode === 304) {
      errorMessage += 'The content has not been modified since your last request.';
    } else if (statusCode === 401) {
      errorMessage += 'Authentication required. Please log in again.';
    } else {
      errorMessage += 'Please check your network connection and try again.';
    }
    
    // Display error using the app's standard pattern
    console.error(errorMessage);
  }

  public downloadfile() {
    const filepath = document.getElementById('filepath')?.innerText || ''
    const filename = document.getElementById('filename')?.innerText || ''
    const filesget = { filepath: filepath, filename: filename.toString() }
    const getfileext =
      filename
        .toString()
        .substring(
          filename.toString().lastIndexOf('.') + 1,
          filename.toString().length,
        ) || filename
    if (getfileext.toString().toLowerCase() == 'docx') {
      this.ContentType = 'application/docx'
    }
    if (getfileext.toString().toLowerCase() == 'jpg') {
      this.ContentType = 'image/jpeg jpeg jpg jpe'
    }
    if (getfileext.toString().toLowerCase() == 'pdf') {
      this.ContentType = 'application/pdf'
    }
    const f = filename.replace(/^.*[\\/]/, '')
    this.file.downloads(filesget).subscribe((response: any) => {
      const file = new Blob([response], { type: this.ContentType })
      const fileURL = URL.createObjectURL(file)
      saveAs(fileURL, f)
    })
  }

  // File Manager's file drag start event function
  onFileDragStart(args: any) {
    console.log("File drag start");
  }

  // File Manager's file drag stop event function
  onFileDragStop(args: any) {
    console.log("File drag stop");
  }

  // File Manager's file dragging event function
  onFileDragging(args: any) {
    console.log("File dragging");
  }

  // File Manager's file dropped event function
  onFileDropped(args: any) {
    const targetFolder = args.targetFolder;
    if (targetFolder) {
      console.log('File dropped in folder:', targetFolder);
      // Handle the file upload here if needed
    }
  }

  public onSuccess() {
    console.log('Ajax request successful')
  }

  // Captures files uploaded during this session so the list can be flushed to
  // the backend on close. Bound from the template via (success)="onAjaxSuccess($event)".
  public onAjaxSuccess(args: any): void {
    console.log('[FM diag] success action =', args?.action, args)
    if (!args) {
      return
    }
    const action = (args.action || '').toString().toLowerCase()
    if (action !== 'upload') {
      return
    }
    const names = this.extractUploadedFileNames(args)
    for (const name of names) {
      if (name && this.uploadedFiles.indexOf(name) === -1) {
        this.uploadedFiles.push(name)
      }
    }
  }

  // Large "upload failed" message shown over the FileManager; the FileManager's
  // own inline status is easy to miss.
  public uploadErrorMessage: string | null = null

  // A failed upload is dropped from the session list so it is not reported as
  // uploaded on close (the backend also only alerts for files it saved).
  private handleUploadFailure(args: any): void {
    const action = (args?.action || '').toString().toLowerCase()
    if (action !== 'upload') {
      return
    }
    const failedNames = this.extractUploadedFileNames(args)
    this.uploadedFiles = this.uploadedFiles.filter((name) => failedNames.indexOf(name) === -1)
    const fileText = failedNames.length > 0 ? `"${failedNames.join('", "')}"` : 'Your file'
    this.uploadErrorMessage = `${fileText} did not upload. Please try again.`
    this.cd.detectChanges()
  }

  public dismissUploadError(): void {
    this.uploadErrorMessage = null
  }

  public onAjaxFailure(args: any): void {
    console.error('FileManager ajax failure:', args)
    this.handleUploadFailure(args)
  }

  // Primary upload capture. The FileManager's embedded Uploader bypasses the
  // (beforeSend)/(success) events for uploads, but (uploadListCreate) fires
  // client-side for every file queued for upload and exposes args.fileInfo.
  public onUploadListCreate(args: any): void {
    const name = args?.fileInfo?.name || args?.data?.name
    console.log('[FM diag] uploadListCreate name =', name, args)
    if (name && this.uploadedFiles.indexOf(name) === -1) {
      this.uploadedFiles.push(name)
    }
  }

  private extractUploadedFileNames(args: any): string[] {
    const names: string[] = []
    const fileData = args?.fileData || args?.files
    if (Array.isArray(fileData)) {
      for (const f of fileData) {
        if (typeof f === 'string') {
          names.push(f)
        } else if (f?.name) {
          names.push(f.name)
        } else if (f?.fileName) {
          names.push(f.fileName)
        }
      }
    }
    // Upload endpoint returns the uploaded filename in the response body.
    const response = args?.response?.response ?? args?.response
    if (typeof response === 'string' && response.length > 0 && response.indexOf('<') === -1) {
      names.push(response)
    }
    return names
  }

  ngOnDestroy(): void {
    // FileManager window is closing. If anything was uploaded during this
    // session, hand the list to the backend so it can fire the alert email
    // and clean up the temporary upload-tracking rows.
    console.log('FileManager closing. uploadedFiles =', this.uploadedFiles, 'blotterId =', this.blotterId)
    if (this.uploadedFiles.length === 0 || !this.blotterId) {
      return
    }
    const toSend = [...this.uploadedFiles]
    this.uploadedFiles = []
    this.file.checkfile(this.blotterId, toSend).subscribe({
      next: () => console.log('CheckingFile post succeeded for', toSend),
      error: (err) => console.error('CheckingFile post failed:', err),
    })
  }

  public closeup() {
    // Handle close event
    console.log('File manager closed');
  }

  public onFailure(args: any) {
    console.error('Ajax request has failed:', args);
    
    // Check for network connectivity issues
    if (!navigator.onLine) {
      console.error('Network connection is unavailable');
    }
    
    // Check for specific error types
    if (args && args.error) {
      if (args.error.status === 401) {
        console.error('Authentication error: Unauthorized access');
      } else if (args.error.status === 403) {
        console.error('Authorization error: Forbidden access');
      } else if (args.error.status === 404) {
        console.error('Resource not found');
      } else if (args.error.status === 500) {
        console.error('Server error occurred');
      }
    }
  }

  onFileOpen(args: any): void {
    const selectedItem = args.fileDetails
    this.openFileInNewWindow(selectedItem)
  }

  openFileInNewWindow(selectedItem: any): void {
    const checkfile = this.checkIfPdfExtensionExists(selectedItem.name)
    if (!checkfile) {
      return
    }

    // Open a placeholder window synchronously inside the user-gesture so the
    // browser does not block it. When the PDF blob arrives we navigate this
    // already-open window to the object URL. Without this, calling
    // window.open() inside the async subscribe callback is silently blocked
    // by Chrome/Edge (no popup-blocker icon, no error) - which is why
    // reopening the file manager after an upload appears to "do nothing".
    const newWindow = window.open('', '_blank')

    const filePath = selectedItem.filterPath + '\\' + selectedItem.name
    const forms = [filePath]
    this.pdfService.preparePdfToOpen(forms).subscribe({
      next: ({ token }) => {
        if (newWindow && !newWindow.closed) {
          showPdfInWindow(newWindow, pdfOpenUrl(token, selectedItem.name), selectedItem.name)
        } else {
          console.warn('Unable to open file in new window, downloading instead')
          this.pdfService.getPdfToOpen(forms).subscribe((response) => {
            saveAs(URL.createObjectURL(response), selectedItem.name)
          })
        }
      },
      error: (error) => {
        console.error('Error fetching PDF file:', error)
        if (newWindow && !newWindow.closed) {
          newWindow.close()
        }
      },
    })
  }

  checkIfPdfExtensionExists(filename: string): boolean {
    return filename.toLowerCase().endsWith('.pdf')
  }
}
